import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface CreateDimensionToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

export const CreateDimensionToolCall: React.FC<CreateDimensionToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Parse dimension name from args
  let dimensionName = 'dimension';
  try {
    const parsedArgs = typeof args === 'object' ? args : JSON.parse(argsText);
    dimensionName = parsedArgs?.name || 'dimension';
  } catch (e) {
    // Ignore parse errors
  }

  // Check if the result indicates success
  let isSuccess = false;
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
    } catch (e) {
      isSuccess = !result.toLowerCase().includes('error');
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning
          ? "bg-primary/10 border-b border-primary/20"
          : "bg-muted/50 border-b border-border"
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Creating dimension: <strong>{dimensionName}</strong>
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Creating dimension: <strong>{dimensionName}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    Failed to create dimension
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Creating dimension: <strong>{dimensionName}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Dimension created successfully
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
